import { useState } from "react";
import { ChevronDown, ChevronUp, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    if (expandedSection === index) {
      setExpandedSection(null);
    } else {
      setExpandedSection(index);
    }
  };

  const sections = [
    {
      title: "1. Information We Collect",
      content: (
        <>
          <h4 className="font-semibold mb-2">
            a. Personal Information You Provide
          </h4>
          <ul className="list-disc pl-5 mb-4">
            <li>Name, email address, phone number (optional)</li>
            <li>Employment information (e.g., current job title, industry)</li>
            <li>Educational background</li>
            <li>Quiz responses related to AI readiness</li>
            <li>Payment information (processed securely via Stripe)</li>
          </ul>
          <h4 className="font-semibold mb-2">
            b. Automatically Collected Data
          </h4>
          <ul className="list-disc pl-5">
            <li>IP address, device type, browser type</li>
            <li>Usage data (pages visited, time on platform, quiz behavior)</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </>
      ),
    },
    {
      title: "2. How We Use Your Information",
      content: (
        <>
          <p>We use your information to:</p>
          <ul className="list-disc pl-5 mt-2">
            <li>Provide personalized career assessments and recommendations</li>
            <li>Calculate AI automation risk and career pathway suggestions</li>
            <li>Deliver mentorship matching and session scheduling</li>
            <li>Improve the functionality and performance of the platform</li>
            <li>Communicate updates, offers, or service changes</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>
        </>
      ),
    },
    {
      title: "3. Sharing of Information",
      content: (
        <>
          <p>We may share your information:</p>
          <ul className="list-disc pl-5 mt-2 mb-4">
            <li>With your assigned mentors (to provide relevant guidance)</li>
            <li>
              With third-party service providers (e.g., payment processors,
              analytics tools)
            </li>
            <li>When required by law or to protect legal rights</li>
            <li>With your consent or direction</li>
          </ul>
          <p className="font-medium">
            We do not sell your personal information to third parties.
          </p>
        </>
      ),
    },
    {
      title: "4. Data Retention",
      content:
        "We retain your data for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of your data by contacting us (see Section 9).",
    },
    {
      title: "5. Your Rights and Choices",
      content: (
        <>
          <p>You have the right to:</p>
          <ul className="list-disc pl-5 mt-2">
            <li>Access, update, or delete your personal information</li>
            <li>Opt-out of marketing communications at any time</li>
            <li>Request a copy of the data we hold about you</li>
            <li>Withdraw consent for processing (where applicable)</li>
          </ul>
        </>
      ),
    },
    {
      title: "6. Security",
      content:
        "We take appropriate technical and organizational measures to protect your personal information, including encryption, secure login, and restricted access to data.",
    },
    {
      title: "7. Children's Privacy",
      content:
        "Ontrack Careers is not intended for use by individuals under the age of 16. We do not knowingly collect personal data from children under this age.",
    },
    {
      title: "8. International Users",
      content:
        "If you are accessing our platform from outside Canada, your information may be transferred to, stored, and processed in Canada or other countries where our servers or third-party service providers are located.",
    },
    {
      title: "9. Contact Us",
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this
            Privacy Policy, please contact us at:
          </p>
          <div className="mt-2">
            <p className="font-medium">Ontrack Careers</p>
            <p>Email: support@ontrackcareers.com</p>
            <p>Location: Vancouver, BC, Canada</p>
          </div>
        </>
      ),
    },
    {
      title: "10. Changes to This Privacy Policy",
      content:
        "We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-app notification. Continued use of the platform constitutes acceptance of the revised policy.",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden my-20">
      <div className="bg-indigo-600 text-white px-6 py-4">
        <div className="flex items-center">
          <Shield className="h-6 w-6 mr-2" />
          <h1 className="text-2xl font-bold">
            Privacy Policy for Ontrack Careers
          </h1>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <div>
            <span className="font-semibold">Effective Date:</span> May 1, 2025
          </div>
          <div>
            <span className="font-semibold">Last Updated:</span> April 24, 2025
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">
          At Ontrack Careers ("we", "our", or "us"), your privacy is important
          to us. This Privacy Policy explains how we collect, use, share, and
          protect personal information when you use our platform, services, and
          related features ("Services"). By using Ontrack Careers, you agree to
          the terms outlined in this policy.
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {sections.map((section, index) => (
          <div key={index} className="border-b border-gray-200 last:border-b-0">
            <button
              onClick={() => toggleSection(index)}
              className="flex justify-between items-center w-full px-6 py-4 text-left focus:outline-none hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-lg font-medium text-gray-900">
                {section.title}
              </h2>
              {expandedSection === index ? (
                <ChevronUp className="h-5 w-5 text-indigo-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-indigo-500" />
              )}
            </button>

            {expandedSection === index && (
              <div className="px-6 py-4 bg-gray-50">
                <div className="text-gray-700">{section.content}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-gray-100 px-6 py-4 text-center">
        <p className="text-sm text-gray-600">
          By using Ontrack Careers, you acknowledge that you have read and
          understand this Privacy Policy.
        </p>
      </div>
    </div>
  );
}
